import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { PencilLine, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@design-system';
import { useFieldChip } from '../../hooks/use-reply-nodes';

/**
 * A piece of information in the email. Purple: Waterlily fills it in. Amber: the same words in
 * every email, to be written here instead (click to turn it into words). Grey: Waterlily can't
 * fill it in yet.
 */
export function FieldChipView(props: NodeViewProps) {
  const chip = useFieldChip(props);

  if (chip.kind === 'write') {
    return (
      <NodeViewWrapper
        as="span"
        className="mx-0.5 inline-flex align-baseline"
        contentEditable={false}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={chip.replaceWithWords}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border border-dashed border-[#E9B872] bg-[#FFF4E5] px-2 text-sm font-semibold leading-[26px] text-[#8A4B00]',
                props.selected && 'ring-2 ring-[#5B3FD6]/40',
              )}
            >
              <PencilLine
                className="size-3.5"
                aria-hidden
              />
              Write: {chip.label}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            The same in every email. Click to replace it with the words to send.
          </TooltipContent>
        </Tooltip>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      data-drag-handle
      title={
        chip.kind === 'none' ? 'Waterlily can’t fill this in yet' : (chip.meaning ?? undefined)
      }
      className={cn(
        'mx-0.5 inline-flex cursor-grab items-center gap-0.5 rounded-md pl-2 pr-0.5 align-baseline text-sm font-semibold leading-[26px]',
        chip.kind === 'code'
          ? 'bg-[#EEE9FF] text-[#3B1FA8]'
          : 'border border-dashed border-zinc-300 bg-zinc-100 text-zinc-600',
        props.selected && 'ring-2 ring-[#5B3FD6]/40',
      )}
    >
      {chip.label}
      {chip.kind === 'none' && <span className="font-normal"> (not available yet)</span>}
      <button
        type="button"
        aria-label={`Remove ${chip.label}`}
        onClick={chip.remove}
        className="text-current/70 inline-flex size-[22px] items-center justify-center rounded hover:bg-black/5"
      >
        <X
          className="size-3"
          aria-hidden
        />
      </button>
    </NodeViewWrapper>
  );
}
